import React, { useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import { EVENT_GROUPS, EVENT_TYPES } from './features/registry';
import { FILTER_ICONS, getPresetDates } from './TopBar';
import './FilterDrawer.css';
import { t } from './i18n';

export const FILTER_SECTIONS = [
  {
    id: 'period',
    label: t('web.filter.period'),
    defaultOpen: true,
  },
  ...EVENT_GROUPS.map(group => ({
    id: group.group,
    label: group.group,
    defaultOpen: true,
    types: group.types.map(type => type.key),
  })),
];

const PERIOD_SHORTCUTS = [
  { label: '7d', key: '7d' },
  { label: '30d', key: '30d' },
  { label: '3m', key: '3m' },
  { label: '6m', key: '6m' },
  { label: '1a', key: 'year' },
  { label: 'Tudo', key: 'all' },
];

const EVENT_TYPE_LABELS = new Map(EVENT_TYPES.map(type => [type.key, type.label]));

export function normalizeDataTypeSearch(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

export function filterDataTypeSections(sections, searchTerm) {
  const query = normalizeDataTypeSearch(searchTerm);

  if (!query) {
    return sections.map(section => ({
      ...section,
      visibleTypes: section.types || [],
    }));
  }

  return sections.flatMap(section => {
    if (!section.types) return [];

    const sectionMatches = normalizeDataTypeSearch(section.label).includes(query);
    const visibleTypes = sectionMatches
      ? section.types
      : section.types.filter(typeKey => {
        const label = EVENT_TYPE_LABELS.get(typeKey) || typeKey;
        return normalizeDataTypeSearch(`${label} ${typeKey}`).includes(query);
      });

    return visibleTypes.length > 0 ? [{ ...section, visibleTypes }] : [];
  });
}

export function filterDataQualityIssues(issues, sourceFilter, severityFilter) {
  return (issues || []).filter(issue => {
    if (sourceFilter !== 'all' && issue.source !== sourceFilter) return false;
    if (severityFilter !== 'all' && issue.severity !== severityFilter) return false;
    return true;
  });
}

function FilterDrawer({
  isOpen,
  isInline = false,
  onClose,
  onCollapse,
  currentFilters,
  onApplyFilters,
  preserveViewport,
  onPreserveViewportChange,
  clusterMarkers,
  onClusterMarkersChange,
  showDwellHeatmap = false,
  onDwellHeatmapChange,
  onVenueSearch,
  onVenueSelect,
  onCitySearch,
  onCitySelect,
  tagFilter = '',
  onTagFilterApply,
  onTagFilterClear,
  tagFilterActive = false,
  tagFilterCount = 0,
  routeSearchTerm,
  onRouteSearchTermChange,
  routeItems = [],
  filteredRouteItems = [],
  selectedRouteId,
  onRouteSelect,
  dataQualityReport,
  hiddenQualityIssueIds = [],
  onToggleHiddenQualityIssue,
  onApplyQualityCorrection,
  onOpenImport,
  trips = [],
  selectedTripId,
  onTripSelect,
  privacyConfig,
  onPrivacyConfigChange,
}) {
  // Local draft state — only applied when user clicks "Aplicar"
  const [draft, setDraft] = useState({ ...currentFilters });
  const [dataTypeSearch, setDataTypeSearch] = useState('');
  const [venueSearch, setVenueSearch] = useState('');
  const [venueResults, setVenueResults] = useState([]);
  const [venueLoading, setVenueLoading] = useState(false);
  const [citySearch, setCitySearch] = useState('');
  const [cityResults, setCityResults] = useState([]);
  const [cityLoading, setCityLoading] = useState(false);
  const [tagFilterInput, setTagFilterInput] = useState(tagFilter);
  const [qualityError, setQualityError] = useState('');
  const [qualityFixingIssueId, setQualityFixingIssueId] = useState(null);
  const [dataQualitySourceFilter, setDataQualitySourceFilter] = useState('all');
  const [dataQualitySeverityFilter, setDataQualitySeverityFilter] = useState('all');
  const sectionRefs = useRef(new Map());
  const [collapsedSections, setCollapsedSections] = useState(() => {
    const initial = {};
    FILTER_SECTIONS.forEach(s => {
      if (!s.defaultOpen) initial[s.id] = true;
    });
    return initial;
  });

  // Sync draft when drawer opens or external filters change
  useEffect(() => {
    if (isOpen) {
      setDraft({ ...currentFilters });
    }
  }, [isOpen, currentFilters]);

  useEffect(() => {
    if (!venueSearch || venueSearch.length < 2) {
      setVenueResults([]);
      return;
    }
    setVenueLoading(true);
    const timer = setTimeout(async () => {
      try {
        const results = await onVenueSearch(venueSearch);
        setVenueResults(results || []);
      } catch (_) {
        setVenueResults([]);
      } finally {
        setVenueLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [venueSearch, onVenueSearch]);

  useEffect(() => {
    if (!citySearch || citySearch.length < 2) {
      setCityResults([]);
      return;
    }
    setCityLoading(true);
    const timer = setTimeout(async () => {
      try {
        const results = await onCitySearch(citySearch);
        setCityResults(results || []);
      } catch (_) {
        setCityResults([]);
      } finally {
        setCityLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [citySearch, onCitySearch]);

  const visibleFilterSections = useMemo(
    () => filterDataTypeSections(FILTER_SECTIONS, dataTypeSearch),
    [dataTypeSearch]
  );
  const visibleTypeCount = visibleFilterSections.reduce(
    (total, section) => total + section.visibleTypes.length,
    0
  );
  const dataQualityIssues = useMemo(
    () => dataQualityReport?.issues || [],
    [dataQualityReport]
  );
  const filteredDataQualityIssues = useMemo(
    () => filterDataQualityIssues(dataQualityIssues, dataQualitySourceFilter, dataQualitySeverityFilter),
    [dataQualityIssues, dataQualitySourceFilter, dataQualitySeverityFilter]
  );
  const dataQualitySources = useMemo(
    () => [...new Set(dataQualityIssues.map(issue => issue.source))],
    [dataQualityIssues]
  );
  const dataQualitySourceLabels = useMemo(
    () => Object.fromEntries(
      dataQualityIssues.map(issue => [issue.source, issue.sourceLabel || issue.source])
    ),
    [dataQualityIssues]
  );
  const dataQualitySeverities = useMemo(
    () => [...new Set(dataQualityIssues.map(issue => issue.severity))],
    [dataQualityIssues]
  );

  // Há seleção/datas ainda não aplicadas? (para sinalizar o botão "Aplicar")
  const hasPendingChanges = useMemo(() => {
    if (draft.startDate !== currentFilters.startDate) return true;
    if (draft.endDate !== currentFilters.endDate) return true;
    const a = [...(draft.selectedTypes || [])].sort();
    const b = [...(currentFilters.selectedTypes || [])].sort();
    return a.length !== b.length || a.some((type, i) => type !== b[i]);
  }, [draft, currentFilters]);

  const toggleSection = (id) => {
    setCollapsedSections(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const navigateToSection = (sectionId) => {
    if (!sectionId) return;

    setDataTypeSearch('');
    setCollapsedSections(prev => ({ ...prev, [sectionId]: false }));

    const scheduleNavigation = window.requestAnimationFrame || (callback => callback());
    scheduleNavigation(() => {
      const section = sectionRefs.current.get(sectionId);
      if (!section) return;
      section.scrollIntoView({ behavior: 'smooth', block: 'start' });
      section.querySelector('.fd-section-header')?.focus({ preventScroll: true });
    });
  };

  const toggleType = (typeKey) => {
    setDraft(prev => {
      const isActive = prev.selectedTypes.includes(typeKey);
      return {
        ...prev,
        selectedTypes: isActive
          ? prev.selectedTypes.filter(t => t !== typeKey)
          : [...prev.selectedTypes, typeKey],
      };
    });
  };

  const setDate = (field, value) => {
    setDraft(prev => ({ ...prev, [field]: value }));
  };

  const applyShortcut = (key) => {
    const dates = getPresetDates(key);
    setDraft(prev => ({ ...prev, ...dates }));
  };

  const handleClear = () => {
    setDraft({
      startDate: '2010-01-01',
      endDate: moment().format('YYYY-MM-DD'),
      selectedTypes: EVENT_TYPES.map(et => et.key),
    });
  };

  const handleApply = () => {
    onApplyFilters(draft);
    onClose();
  };

  // Count active types per section
  const getSectionCount = (section) => {
    if (!section.types) return null;
    const active = section.types.filter(t => draft.selectedTypes.includes(t)).length;
    return `${active}/${section.types.length}`;
  };

  const getTypeLabel = (key) => EVENT_TYPE_LABELS.get(key) || key;
  const hiddenIssueIdSet = useMemo(
    () => new Set(hiddenQualityIssueIds),
    [hiddenQualityIssueIds]
  );

  const handleQualityFix = async (issue) => {
    if (!onApplyQualityCorrection) return;
    setQualityError('');
    setQualityFixingIssueId(issue.id);
    try {
      await onApplyQualityCorrection(issue);
    } catch (error) {
      setQualityError(error.message || t('web.quality.applyFail'));
    } finally {
      setQualityFixingIssueId(null);
    }
  };

  return (
    <>
      {isOpen && !isInline && (
        <button
          type="button"
          className="filter-drawer-backdrop"
          aria-label={t('web.filter.close')}
          onClick={onClose}
        />
      )}
      <aside className={`filter-drawer ${isOpen ? 'is-open' : ''} ${isInline ? 'is-inline' : ''}`} aria-hidden={!isOpen}>
        {/* Header */}
        <div className="filter-drawer-header">
          <div className="filter-drawer-header-left">
            <h2>Filtros</h2>
          </div>
          <button type="button" className="filter-drawer-clear-btn" onClick={handleClear}>
            Limpar tudo
          </button>
          {onCollapse && (
            <button
              type="button"
              className="filter-drawer-collapse"
              onClick={onCollapse}
              aria-label="Recolher filtros"
              title="Recolher filtros"
            >
              ◂
            </button>
          )}
          {!isInline && (
            <button type="button" className="filter-drawer-close" onClick={onClose}>×</button>
          )}
        </div>

        {/* Body */}
        <div className="filter-drawer-body">
          <div className="fd-data-navigator">
            <div className="fd-data-navigator-heading">
              <span>Navegar dados</span>
              <span>{dataTypeSearch ? `${visibleTypeCount}/${EVENT_TYPES.length}` : `${EVENT_TYPES.length} tipos`}</span>
            </div>
            <div className="fd-data-search-row">
              <input
                type="search"
                className="fd-data-search-input"
                aria-label={t('web.filter.searchTypes')}
                placeholder={t('web.filter.searchTypes')}
                value={dataTypeSearch}
                onChange={event => setDataTypeSearch(event.target.value)}
              />
              {dataTypeSearch && (
                <button
                  type="button"
                  className="fd-data-search-clear"
                  aria-label="Limpar busca de tipos"
                  title="Limpar busca"
                  onClick={() => setDataTypeSearch('')}
                >
                  ×
                </button>
              )}
            </div>
            <select
              className="fd-data-section-select"
              aria-label={t('web.filter.goToSection')}
              value=""
              onChange={event => navigateToSection(event.target.value)}
            >
              <option value="" disabled>{t('web.filter.goToSection.option')}</option>
              {FILTER_SECTIONS.map(section => (
                <option key={section.id} value={section.id}>{section.label}</option>
              ))}
            </select>
          </div>

          {dataTypeSearch && visibleFilterSections.length === 0 && (
            <p className="fd-data-search-empty">Nenhum tipo de dado encontrado.</p>
          )}

          {visibleFilterSections.map(section => {
            const isCollapsed = !dataTypeSearch && collapsedSections[section.id];
            const count = getSectionCount(section);

            return (
              <div
                key={section.id}
                className="fd-section"
                data-filter-section={section.id}
                ref={node => {
                  if (node) sectionRefs.current.set(section.id, node);
                  else sectionRefs.current.delete(section.id);
                }}
              >
                <button
                  type="button"
                  className="fd-section-header"
                  onClick={() => toggleSection(section.id)}
                >
                  <span className="fd-section-label">
                    {section.id === 'period' ? '1. ' : ''}{section.label}
                  </span>
                  {count && <span className="fd-section-count">{count}</span>}
                  <span className={`fd-chevron ${isCollapsed ? 'collapsed' : ''}`}>▼</span>
                </button>

                {!isCollapsed && (
                  <div className="fd-section-body">
                    {section.id === 'period' ? (
                      <>
                        <div className="fd-period-dates">
                          <label>
                            {t('web.filter.start')}
                            <input
                              type="date"
                              value={draft.startDate}
                              onChange={e => setDate('startDate', e.target.value)}
                            />
                          </label>
                          <label>
                            Fim
                            <input
                              type="date"
                              value={draft.endDate}
                              onChange={e => setDate('endDate', e.target.value)}
                            />
                          </label>
                        </div>
                        <div className="fd-period-shortcuts">
                          {PERIOD_SHORTCUTS.map(s => (
                            <button
                              key={s.key}
                              type="button"
                              className="fd-shortcut-btn"
                              onClick={() => applyShortcut(s.key)}
                            >
                              {s.label}
                            </button>
                          ))}
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="fd-toggles">
                          {section.visibleTypes.map(typeKey => (
                            <button
                              key={typeKey}
                              type="button"
                              className={`fd-toggle ${draft.selectedTypes.includes(typeKey) ? 'is-active' : ''}`}
                              onClick={() => toggleType(typeKey)}
                            >
                              <span className="fd-toggle-main">
                                <span className="fd-toggle-icon">{FILTER_ICONS[typeKey] || '🔘'}</span>
                                <span>{getTypeLabel(typeKey)}</span>
                              </span>
                              <span className="fd-toggle-check">✓</span>
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          <div className="fd-section fd-venue-search">
            <div className="fd-section-header fd-section-header-static">
              <span className="fd-section-label">{t('web.filter.searchPlace')}</span>
              {venueLoading && <span className="fd-section-count">…</span>}
            </div>
            <div className="fd-section-body">
              <input
                type="search"
                className="route-search-input"
                aria-label={t('web.filter.searchPlace.a11y')}
                placeholder="Nome do local…"
                value={venueSearch}
                onChange={e => setVenueSearch(e.target.value)}
              />
              {venueSearch.length >= 2 && !venueLoading && venueResults.length === 0 && (
                <p className="route-search-empty">Nenhum local encontrado.</p>
              )}
              {venueResults.length > 0 && (
                <div className="route-search-results" role="list">
                  {venueResults.map((venue, i) => (
                    <button
                      key={i}
                      type="button"
                      className="route-search-item"
                      onClick={() => {
                        onVenueSelect(venue);
                        setVenueSearch('');
                        setVenueResults([]);
                      }}
                    >
                      <span className="route-search-title">{venue.name}</span>
                      <span className="route-search-meta">
                        {venue.category || 'Local'}{venue.times_visited ? ` · ${venue.times_visited}×` : ''}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="fd-section fd-city-search">
            <div className="fd-section-header fd-section-header-static">
              <span className="fd-section-label">{t('web.filter.searchCity')}</span>
              {cityLoading && <span className="fd-section-count">…</span>}
            </div>
            <div className="fd-section-body">
              <input
                type="search"
                className="route-search-input"
                aria-label={t('web.filter.searchCity.a11y')}
                placeholder="Nome da cidade…"
                value={citySearch}
                onChange={e => setCitySearch(e.target.value)}
              />
              {citySearch.length >= 2 && !cityLoading && cityResults.length === 0 && (
                <p className="route-search-empty">Nenhuma cidade encontrada.</p>
              )}
              {cityResults.length > 0 && (
                <div className="route-search-results" role="list">
                  {cityResults.map((city, i) => (
                    <button
                      key={i}
                      type="button"
                      className="route-search-item"
                      onClick={() => {
                        onCitySelect(city);
                        setCitySearch('');
                        setCityResults([]);
                      }}
                    >
                      <span className="route-search-title">{city.city}</span>
                      <span className="route-search-meta">
                        {[city.state, city.country].filter(Boolean).join(', ') || city.country || ''}{city.count ? ` · ${city.count} locais` : ''}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="fd-section fd-tag-filter">
            <div className="fd-section-header fd-section-header-static">
              <span className="fd-section-label">Filtrar por tag</span>
              {tagFilterActive && <span className="fd-section-count">{tagFilterCount}</span>}
            </div>
            <div className="fd-section-body">
              <input
                type="search"
                className="route-search-input"
                aria-label="Filtrar locais por tag"
                placeholder="Nome da tag…"
                value={tagFilterInput}
                onChange={e => setTagFilterInput(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && onTagFilterApply) onTagFilterApply(tagFilterInput);
                }}
              />
              <div className="fd-tag-filter-actions">
                <button type="button" onClick={() => onTagFilterApply && onTagFilterApply(tagFilterInput)}>
                  {t('web.filter.apply')}
                </button>
                {tagFilterActive && (
                  <button
                    type="button"
                    onClick={() => {
                      setTagFilterInput('');
                      if (onTagFilterClear) onTagFilterClear();
                    }}
                  >
                    Limpar
                  </button>
                )}
              </div>
              {tagFilterActive && tagFilterCount === 0 && (
                <p className="route-search-empty">Nenhum local com essa tag.</p>
              )}
            </div>
          </div>
          <div className="fd-section fd-route-search">
            <div className="fd-section-header fd-section-header-static">
              <span className="fd-section-label">{t('web.filter.searchRoutes')}</span>
              <span className="fd-section-count">{filteredRouteItems.length}/{routeItems.length}</span>
            </div>
            <div className="fd-section-body">
              <input
                type="search"
                className="route-search-input"
                aria-label={t('web.filter.searchRoutes.a11y')}
                placeholder={t('web.filter.searchRoutes.ph')}
                value={routeSearchTerm}
                onChange={(event) => onRouteSearchTermChange(event.target.value)}
              />
              {routeItems.length === 0 ? (
                <p className="route-search-empty">{t('web.filter.noRoutes')}</p>
              ) : filteredRouteItems.length === 0 ? (
                <p className="route-search-empty">Nenhuma rota encontrada.</p>
              ) : (
                <div className="route-search-results" role="list">
                  {filteredRouteItems.map(routeItem => (
                    <button
                      key={routeItem.id}
                      type="button"
                      className={`route-search-item ${selectedRouteId === routeItem.id ? 'is-selected' : ''}`}
                      aria-pressed={selectedRouteId === routeItem.id}
                      onClick={() => onRouteSelect(routeItem.id)}
                    >
                      <span className="route-search-title">{routeItem.label}</span>
                      <span className="route-search-meta">
                        {routeItem.dateLabel} · {routeItem.source} · {routeItem.typeLabel}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="fd-section fd-trip-search">
            <div className="fd-section-header fd-section-header-static">
              <span className="fd-section-label">Viagens</span>
              <span className="fd-section-count">{trips.length}</span>
            </div>
            <div className="fd-section-body">
              {trips.length === 0 ? (
                <p className="route-search-empty">Nenhuma viagem detectada. Viaje para longe da sua cidade-base!</p>
              ) : (
                <div className="route-search-results" role="list">
                  {trips.map(trip => (
                    <button
                      key={trip.id}
                      type="button"
                      className={`route-search-item ${selectedTripId === trip.id ? 'is-selected' : ''}`}
                      aria-pressed={selectedTripId === trip.id}
                      onClick={() => onTripSelect && onTripSelect(trip.id)}
                    >
                      <span className="route-search-title">{trip.mainCity ? `${trip.title} ${trip.mainCountry ? `(${trip.mainCountry})` : ''}` : trip.title}</span>
                      <span className="route-search-meta">
                        {trip.startDate} → {trip.endDate} · {trip.events.length} eventos
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="fd-section fd-data-quality">
            <div className="fd-section-header fd-section-header-static">
              <span className="fd-section-label">Qualidade dos dados</span>
              <span className="fd-section-count">
                {filteredDataQualityIssues.length}/{dataQualityIssues.length}
              </span>
            </div>
            <div className="fd-section-body">
              {qualityError && (
                <p className="fd-quality-error" role="alert">{qualityError}</p>
              )}
              <div className="fd-quality-filters">
                <select
                  className="fd-data-section-select fd-quality-select"
                  aria-label="Filtrar problemas por fonte"
                  value={dataQualitySourceFilter}
                  onChange={event => setDataQualitySourceFilter(event.target.value)}
                >
                  <option value="all">Todas as fontes</option>
                  {dataQualitySources.map(source => (
                    <option key={source} value={source}>{dataQualitySourceLabels[source] || source}</option>
                  ))}
                </select>
                <select
                  className="fd-data-section-select fd-quality-select"
                  aria-label="Filtrar problemas por severidade"
                  value={dataQualitySeverityFilter}
                  onChange={event => setDataQualitySeverityFilter(event.target.value)}
                >
                  <option value="all">Todas as severidades</option>
                  {dataQualitySeverities.map(severity => (
                    <option key={severity} value={severity}>{severity}</option>
                  ))}
                </select>
              </div>
              {dataQualityIssues.length === 0 ? (
                <p className="route-search-empty">Nenhum problema detectado no recorte atual.</p>
              ) : filteredDataQualityIssues.length === 0 ? (
                <p className="route-search-empty">Nenhum problema com esses filtros.</p>
              ) : (
                <div className="route-search-results fd-quality-results" role="list">
                  {filteredDataQualityIssues.map(issue => (
                    <div
                      key={issue.id}
                      className={`route-search-item fd-quality-item severity-${issue.severity}`}
                    >
                      <span className="route-search-title">{issue.title}</span>
                      <span className="route-search-meta">
                        {issue.sourceLabel} · {issue.severity}{issue.count > 1 ? ` · ${issue.count} itens` : ''}{issue.date ? ` · ${issue.date}` : ''}
                      </span>
                      <span className="route-search-summary">{issue.description}</span>
                      <div className="fd-quality-actions">
                        {issue.canHide && onToggleHiddenQualityIssue && (
                          <button
                            type="button"
                            className="fd-quality-action-btn"
                            onClick={() => onToggleHiddenQualityIssue(issue.id)}
                          >
                            {hiddenIssueIdSet.has(issue.id) ? 'Mostrar novamente' : 'Esconder do mapa'}
                          </button>
                        )}
                        {issue.canFix && onApplyQualityCorrection && (
                          <button
                            type="button"
                            className="fd-quality-action-btn is-danger"
                            onClick={() => handleQualityFix(issue)}
                            disabled={qualityFixingIssueId === issue.id}
                          >
                            {qualityFixingIssueId === issue.id ? 'Corrigindo...' : 'Corrigir no banco'}
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <label className="fd-preserve-viewport">
            <input
              type="checkbox"
              checked={preserveViewport}
              onChange={e => onPreserveViewportChange(e.target.checked)}
            />
            {t('web.filter.preserveMap')}
          </label>
          <label className="fd-preserve-viewport">
            <input
              type="checkbox"
              checked={clusterMarkers}
              onChange={e => onClusterMarkersChange(e.target.checked)}
            />
            Clusterizar marcadores no mapa
          </label>
          {onDwellHeatmapChange && (
            <label className="fd-preserve-viewport">
              <input
                type="checkbox"
                checked={showDwellHeatmap}
                onChange={e => onDwellHeatmapChange(e.target.checked)}
              />
              Mostrar mapa de calor de tempo gasto
            </label>
          )}
          {privacyConfig && onPrivacyConfigChange && (
            <div className="fd-section fd-privacy-section">
              <div className="fd-section-header fd-section-header-static">
                <span className="fd-section-label">Privacidade</span>
              </div>
              <div className="fd-section-body">
                <label className="fd-preserve-viewport">
                  <input
                    type="checkbox"
                    checked={privacyConfig.enabled}
                    onChange={e => onPrivacyConfigChange({ ...privacyConfig, enabled: e.target.checked })}
                  />
                  {t('web.filter.privateMode')}
                </label>
                {privacyConfig.enabled && (
                  <p className="route-search-empty" style={{ marginTop: 8 }}>
                    {t('web.filter.privateHint')}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="filter-drawer-footer">
          <button type="button" className="fd-footer-clear" onClick={handleClear}>
            Limpar filtros
          </button>
          {isInline && onOpenImport && (
            <button type="button" className="fd-footer-import" onClick={onOpenImport}>
              Importar dados
            </button>
          )}
          <button
            type="button"
            className={`fd-footer-apply ${hasPendingChanges ? 'has-pending' : ''}`}
            onClick={handleApply}
          >
            {hasPendingChanges ? t('web.filter.applyChanges') : t('web.filter.applyFilters')}
          </button>
        </div>
      </aside>
    </>
  );
}

export default FilterDrawer;
